import { cn } from "@core/utils";
import { TextArea } from "./TextArea";
import { TextField } from "./TextField";

/** A text in the two interface languages. */
export interface BilingualText {
  fr: string;
  en: string;
}

interface BilingualFieldProps {
  /** The field's name without the language: « Question », « Présentation de l'appel ». */
  label: string;
  value: BilingualText;
  onChange: (value: BilingualText) => void;
  onBlur?: () => void;
  /** A text box of several lines (the presentation) instead of one line. */
  multiline?: boolean;
  /**
   * `stack`: French above English, as in the question drawer. `columns`: side by side from `sm`,
   * as in the appel's candidate texts.
   */
  layout?: "stack" | "columns";
  isDisabled?: boolean;
  /** One message per language, shown under its own field. */
  errorMessage?: { fr?: string; en?: string };
  className?: string;
}

/**
 * One piece of content written twice, in French and in English: « Question (FR) » and
 * « Question (EN) ». Both are required wherever this is used (the API rejects a missing language),
 * so each field shows its own error. Used for a question, an option, a follow-up and the texts the
 * applicants will read, so they all label and lay out the pair the same way.
 */
export function BilingualField({
  label,
  value,
  onChange,
  onBlur,
  multiline = false,
  layout = "stack",
  isDisabled,
  errorMessage,
  className,
}: BilingualFieldProps) {
  const Field = multiline ? TextArea : TextField;

  return (
    <div
      className={cn(
        "grid gap-md",
        layout === "columns" ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1",
        className
      )}
    >
      <Field
        label={`${label} (FR)`}
        value={value.fr}
        onChange={(fr) => {
          onChange({ ...value, fr });
        }}
        onBlur={onBlur}
        isDisabled={isDisabled}
        errorMessage={errorMessage?.fr}
      />
      <Field
        label={`${label} (EN)`}
        value={value.en}
        onChange={(en) => {
          onChange({ ...value, en });
        }}
        onBlur={onBlur}
        isDisabled={isDisabled}
        errorMessage={errorMessage?.en}
      />
    </div>
  );
}

export type { BilingualFieldProps };
